import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL || '';

const ACCEPTED_EXTS = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.tiff', '.tif', '.webp', '.ico', '.svg', '.ppm', '.pgm', '.pbm'];
const ACCEPT_MIME = {
  'image/*': ACCEPTED_EXTS,
};

const arrowBtn = {
  background: 'none',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: '4px',
  color: 'rgba(255,255,255,0.5)',
  cursor: 'pointer',
  padding: '2px 6px',
  fontSize: '11px',
  lineHeight: 1.4,
};

export default function MergeImagesPdfCard({ lang }) {
  const { token } = useAuth();
  const [files, setFiles]     = useState([]);
  const [previews, setPreviews] = useState({});
  const [loading, setLoading] = useState(false);

  const onDrop = useCallback((accepted) => {
    const imgs = accepted.filter(f => {
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      return ACCEPTED_EXTS.includes(ext);
    });
    if (imgs.length < accepted.length) {
      toast.error(lang === 'ar' ? 'بعض الملفات غير مدعومة' : lang === 'fa' ? 'برخی فایل‌ها پشتیبانی نمی‌شوند' : 'Some files are unsupported');
    }
    setFiles(prev => {
      const combined = [...prev, ...imgs];
      if (combined.length > 50) {
        toast.error(lang === 'ar' ? 'الحد الأقصى 50 صورة' : lang === 'fa' ? 'حداکثر ۵۰ تصویر' : 'Maximum 50 images');
        return combined.slice(0, 50);
      }
      return combined;
    });
    // generate previews for new image files
    imgs.forEach(f => {
      if (f.type.startsWith('image/') && !f.name.endsWith('.svg')) {
        const reader = new FileReader();
        reader.onload = (e) => setPreviews(prev => ({ ...prev, [f.name + f.lastModified]: e.target.result }));
        reader.readAsDataURL(f);
      }
    });
  }, [lang]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPT_MIME,
    multiple: true,
  });

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const moveUp = (idx) => {
    if (idx === 0) return;
    setFiles(prev => { const n = [...prev]; [n[idx - 1], n[idx]] = [n[idx], n[idx - 1]]; return n; });
  };
  const moveDown = (idx) => {
    setFiles(prev => {
      if (idx >= prev.length - 1) return prev;
      const n = [...prev]; [n[idx], n[idx + 1]] = [n[idx + 1], n[idx]]; return n;
    });
  };

  const previewKey = (f) => f.name + f.lastModified;

  const handleMerge = async () => {
    if (files.length < 1) {
      toast.error(lang === 'ar' ? 'أضف صورة واحدة على الأقل' : lang === 'fa' ? 'حداقل یک تصویر اضافه کنید' : 'Add at least one image');
      return;
    }
    setLoading(true);
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await axios.post(`${API}/api/convert/merge-images-to-pdf`, formData, {
        responseType: 'blob',
        headers,
        timeout: 120000,
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'merged_images.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(
        lang === 'ar' ? 'تم التحويل بنجاح! 🎉' : lang === 'fa' ? 'تبدیل موفقیت‌آمیز بود! 🎉' : 'Done! PDF downloaded 🎉',
        { style: { background: '#13131f', color: '#00d4aa', border: '1px solid rgba(0,212,170,0.3)' } }
      );
    } catch (err) {
      let msg = lang === 'ar' ? 'فشل التحويل' : lang === 'fa' ? 'تبدیل ناموفق بود' : 'Conversion failed';
      if (err.response?.data instanceof Blob) {
        try { const text = await err.response.data.text(); msg = JSON.parse(text).detail || msg; } catch { }
      } else if (err.response?.data?.detail) {
        msg = err.response.data.detail;
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const L = {
    drop:    lang === 'ar' ? 'اسحب الصور هنا أو انقر للاختيار' : lang === 'fa' ? 'تصاویر را اینجا بکشید یا کلیک کنید' : 'Drag images here or click to select',
    formats: lang === 'ar' ? 'PNG · JPG · GIF · BMP · TIFF · WebP · SVG · ICO وغيرها' : lang === 'fa' ? 'PNG · JPG · GIF · BMP · TIFF · WebP · SVG · ICO و بیشتر' : 'PNG · JPG · GIF · BMP · TIFF · WebP · SVG · ICO & more',
    order:   lang === 'ar' ? 'ترتيب الصفحات' : lang === 'fa' ? 'ترتیب صفحات' : 'Page order',
    merge:   lang === 'ar' ? '⚡ تحويل إلى PDF' : lang === 'fa' ? '⚡ تبدیل به PDF' : '⚡ Convert to PDF',
    merging: lang === 'ar' ? 'جاري التحويل...' : lang === 'fa' ? 'در حال تبدیل...' : 'Converting...',
    clear:   lang === 'ar' ? 'مسح الكل' : lang === 'fa' ? 'پاک کردن' : 'Clear All',
  };

  return (
    <div>
      {/* Drop zone */}
      <div
        {...getRootProps()}
        style={{
          border: `2px dashed ${isDragActive ? '#8b5cf6' : 'rgba(255,255,255,0.15)'}`,
          borderRadius: '12px',
          padding: '32px 20px',
          textAlign: 'center',
          cursor: 'pointer',
          background: isDragActive ? 'rgba(139,92,246,0.06)' : 'rgba(255,255,255,0.02)',
          transition: 'all 0.2s',
          marginBottom: '16px',
        }}
      >
        <input {...getInputProps()} />
        <div style={{ fontSize: '32px', marginBottom: '8px' }}>🖼️</div>
        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>{L.drop}</div>
        <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px', marginTop: '6px' }}>{L.formats}</div>
        <div style={{ color: 'rgba(255,255,255,0.2)', fontSize: '11px', marginTop: '4px' }}>
          {lang === 'ar' ? 'حتى 50 صورة' : lang === 'fa' ? 'تا ۵۰ تصویر' : 'up to 50 images'}
        </div>
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{
            fontSize: '12px', fontWeight: 700, color: 'rgba(255,255,255,0.4)',
            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span>{L.order} ({files.length})</span>
            <button onClick={() => setFiles([])} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', fontSize: '11px', cursor: 'pointer' }}>
              {L.clear}
            </button>
          </div>

          {files.map((f, i) => (
            <div key={previewKey(f)} style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: 'rgba(255,255,255,0.04)', borderRadius: '8px',
              padding: '6px 10px', marginBottom: '6px',
              border: '1px solid rgba(255,255,255,0.08)',
            }}>
              {/* Thumbnail */}
              {previews[previewKey(f)] ? (
                <img
                  src={previews[previewKey(f)]}
                  alt=""
                  style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }}
                />
              ) : (
                <div style={{
                  width: 32, height: 32, borderRadius: '4px', flexShrink: 0,
                  background: 'rgba(139,92,246,0.2)', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: '14px',
                }}>
                  🖼️
                </div>
              )}
              <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px', minWidth: '18px' }}>{i + 1}.</span>
              <span style={{ flex: 1, fontSize: '13px', color: 'rgba(255,255,255,0.8)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {f.name}
              </span>
              <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', flexShrink: 0 }}>
                {(f.size / 1024).toFixed(0)} KB
              </span>
              <button onClick={() => moveUp(i)}   disabled={i === 0}               style={arrowBtn}>▲</button>
              <button onClick={() => moveDown(i)} disabled={i === files.length - 1} style={arrowBtn}>▼</button>
              <button onClick={() => removeFile(i)} style={{ ...arrowBtn, color: '#e94560' }}>✕</button>
            </div>
          ))}
        </div>
      )}

      <button
        className="btn-primary"
        onClick={handleMerge}
        disabled={files.length < 1 || loading}
        style={{ opacity: files.length < 1 ? 0.5 : 1, background: 'linear-gradient(135deg, #7c3aed, #8b5cf6)' }}
      >
        {loading ? <><span className="spinner" />{L.merging}</> : L.merge}
      </button>
    </div>
  );
}
